/**
 * MCP tools for issue management.
 *
 * Mirrors `canary issues list` and `canary issues get`.
 *
 * @module
 */

import { mcpApiRequest } from '../api-client';
import { toolJson } from '../tool-helpers';

export function getIssueToolDefinitions() {
  return [
    {
      name: 'canary_issues_list',
      description:
        'List and search issues in the current organization. Supports full-text search, severity/status filtering, and pagination.',
      inputSchema: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Full-text search query',
          },
          severity: {
            type: 'string',
            enum: ['low', 'medium', 'high', 'unknown'],
            description: 'Filter by severity level',
          },
          statuses: {
            type: 'string',
            description: 'Comma-separated status filter: open, closed, not_a_bug',
          },
          propertyId: {
            type: 'string',
            description: 'Filter by property UUID',
          },
          page: {
            type: 'number',
            description: 'Page number (default: 1)',
          },
          pageSize: {
            type: 'number',
            description: 'Page size (default: 25)',
          },
        },
      },
    },
    {
      name: 'canary_issues_get',
      description:
        'Get issue detail with diagnostics, including console errors and network failures from the latest occurrence.',
      inputSchema: {
        type: 'object',
        properties: {
          issueId: {
            type: 'string',
            description: 'The issue UUID',
          },
        },
        required: ['issueId'],
      },
    },
  ];
}

export async function handleIssueTool(
  tool: string,
  args: Record<string, unknown>
) {
  if (tool === 'canary_issues_list') {
    try {
      const params = new URLSearchParams();
      if (args.search) params.set('search', String(args.search));
      if (args.severity) params.set('severity', String(args.severity));
      if (args.statuses) params.set('statuses', String(args.statuses));
      if (args.propertyId) params.set('propertyId', String(args.propertyId));
      if (args.page) params.set('page', String(args.page));
      if (args.pageSize) params.set('pageSize', String(args.pageSize));

      const qs = params.toString();
      const path = `/v2/issues${qs ? `?${qs}` : ''}`;
      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown[];
        pagination: unknown;
      }>('GET', path);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data, pagination: result.pagination });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_issues_get') {
    try {
      const issueId = args.issueId as string;

      const detailResult = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: {
          issue: { id: string; latestOccurrence?: { id: string } | null };
          occurrences: unknown[];
          commentCount: number;
        };
      }>('GET', `/v2/issues/${issueId}`);

      if (!detailResult.ok) {
        return toolJson({ ok: false, error: detailResult.error ?? 'Unknown error' });
      }

      const { issue } = detailResult.data;
      const occ = issue.latestOccurrence;

      let consoleErrors: unknown[] = [];
      let networkErrors: unknown[] = [];

      if (occ) {
        try {
          const [consoleRes, networkRes] = await Promise.all([
            mcpApiRequest<{ ok: boolean; data: unknown[] }>(
              'GET',
              `/v2/issues/${issueId}/occurrences/${occ.id}/console-logs`
            ),
            mcpApiRequest<{ ok: boolean; data: unknown[] }>(
              'GET',
              `/v2/issues/${issueId}/occurrences/${occ.id}/network-requests`
            ),
          ]);

          if (consoleRes.ok && Array.isArray(consoleRes.data)) {
            consoleErrors = (consoleRes.data as Array<{ type: string }>).filter(
              (e) => e.type === 'error'
            );
          }

          if (networkRes.ok && Array.isArray(networkRes.data)) {
            networkErrors = (networkRes.data as Array<{ status: number | null }>).filter(
              (e) => e.status === null || e.status === 0 || (e.status !== null && e.status >= 400)
            );
          }
        } catch {
          // Diagnostics are best-effort
        }
      }

      return toolJson({
        ok: true,
        ...detailResult.data,
        consoleErrors,
        networkErrors,
      });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return null;
}
